import { HsLayer } from '../../engine-new/filtering/layer.class';
import type { HsCamera } from '../../engine-new/objects/cameras/camera.class';
import type { HsLayerPostProcess } from '../types/layer-post-process.interface';
import type { HsSamples } from '../types/samples.type';

export class HsGraphicLayer extends HsLayer {
  public antialiasingSamples: HsSamples = 0;

  public readonly texture: WebGLTexture | null;
  /** Single-sample depth copy, allocated only while a post process that needs depth is attached. */
  public depthTexture: WebGLTexture | null = null;
  public postProcess: HsLayerPostProcess | null = null;

  public readonly samplingFramebuffer: WebGLFramebuffer | null;
  public readonly framebuffer: WebGLFramebuffer | null;
  public readonly depthRenderbuffer: WebGLRenderbuffer | null;
  public readonly renderbuffer: WebGLRenderbuffer | null;

  /**
   * Clear to transparent instead of opaque, so the layer composites over the layers drawn before it (gizmos,
   * insets). The scene layer stays opaque.
   */
  public transparentBackground: boolean = false;
  public renderSize: number = 1;
  public hasPresentedFrame: boolean = false;
  public presentDistributedProgress: boolean = true;

  private left: number = 0;
  private top: number = 0;
  private width: number = 0;
  private height: number = 0;

  constructor(camera: HsCamera, gl: WebGL2RenderingContext, samples: HsSamples = 0) {
    super(camera);
    this.antialiasingSamples = samples;
    this.samplingFramebuffer = gl.createFramebuffer();
    this.depthRenderbuffer = gl.createRenderbuffer();
    this.renderbuffer = gl.createRenderbuffer();
    this.framebuffer = gl.createFramebuffer();
    this.texture = gl.createTexture();
    if (
      this.samplingFramebuffer === null ||
      this.framebuffer === null ||
      this.depthRenderbuffer === null ||
      this.renderbuffer === null ||
      this.texture === null
    ) {
      return;
    }
    this.resizeFramebuffer(gl, 1, 1);

    gl.bindFramebuffer(gl.FRAMEBUFFER, this.samplingFramebuffer);
    gl.framebufferRenderbuffer(gl.FRAMEBUFFER, gl.DEPTH_STENCIL_ATTACHMENT, gl.RENDERBUFFER, this.depthRenderbuffer);
    gl.framebufferRenderbuffer(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.RENDERBUFFER, this.renderbuffer);
    gl.bindFramebuffer(gl.FRAMEBUFFER, this.framebuffer);
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, this.texture, 0);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
  }

  public transferToTexture(gl: WebGL2RenderingContext): void {
    gl.bindFramebuffer(gl.FRAMEBUFFER, this.samplingFramebuffer);
    gl.bindFramebuffer(gl.DRAW_FRAMEBUFFER, this.framebuffer);
    const mask: number = this.depthTexture === null ? gl.COLOR_BUFFER_BIT : gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT;
    gl.blitFramebuffer(0, 0, this.width, this.height, 0, 0, this.width, this.height, mask, gl.NEAREST);
    gl.bindFramebuffer(gl.DRAW_FRAMEBUFFER, null);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
  }

  /**
   * Attaches or removes the post process. Depth resolve is allocated only while a post process needs it, so layers
   * without one keep the original framebuffer layout and blit cost.
   */
  public setPostProcess(gl: WebGL2RenderingContext, postProcess: HsLayerPostProcess | null): void {
    this.postProcess = postProcess;
    this.setDepthResolve(gl, postProcess?.requiresDepth === true);
    this.hasPresentedFrame = false;
    this.presentDistributedProgress = true;
  }

  public getLeft(): number {
    return this.left;
  }

  public getTop(): number {
    return this.top;
  }

  public getWidth(): number {
    return this.width;
  }

  public getHeight(): number {
    return this.height;
  }

  public setRect(left: number, top: number, renderSize: number): void {
    this.left = left;
    this.top = top;
    this.renderSize = renderSize;
  }

  public resizeFramebuffer(gl: WebGL2RenderingContext, width: number, height: number): void {
    // A canvas without layout (hidden tab or pane, collapsed container) reports 0x0; zero-size attachments make
    // the framebuffer incomplete, so every clear, draw and blit would raise INVALID_FRAMEBUFFER_OPERATION.
    width = width < 1 ? 1 : width;
    height = height < 1 ? 1 : height;
    const isSizeSceneNotChanged: boolean = this.width === width && this.height === height;
    if (isSizeSceneNotChanged) {
      return;
    }
    this.hasPresentedFrame = false;
    this.presentDistributedProgress = true;
    this.width = width;
    this.height = height;

    this.resizeTexture(gl);
    this.updateSamplingFramebuffer(gl);
    if (this.depthTexture !== null) {
      this.allocateDepthTexture(gl);
    }
  }

  public updateSamplingFramebuffer(gl: WebGL2RenderingContext): void {
    if (this.depthRenderbuffer === null || this.renderbuffer === null) {
      return;
    }
    gl.bindRenderbuffer(gl.RENDERBUFFER, this.depthRenderbuffer);
    gl.renderbufferStorageMultisample(
      gl.RENDERBUFFER,
      this.antialiasingSamples,
      gl.DEPTH24_STENCIL8,
      this.width,
      this.height
    );
    gl.bindRenderbuffer(gl.RENDERBUFFER, this.renderbuffer);
    gl.renderbufferStorageMultisample(gl.RENDERBUFFER, this.antialiasingSamples, gl.RGBA8, this.width, this.height);
    gl.bindRenderbuffer(gl.RENDERBUFFER, null);
  }

  public deleteFramebuffer(gl: WebGL2RenderingContext): void {
    gl.deleteTexture(this.depthTexture);
    gl.deleteTexture(this.texture);
    gl.deleteFramebuffer(this.framebuffer);
    gl.deleteRenderbuffer(this.depthRenderbuffer);
    gl.deleteRenderbuffer(this.renderbuffer);
    gl.deleteFramebuffer(this.samplingFramebuffer);
  }

  private setDepthResolve(gl: WebGL2RenderingContext, enabled: boolean): void {
    if (enabled === (this.depthTexture !== null) || this.framebuffer === null) {
      return;
    }
    gl.bindFramebuffer(gl.FRAMEBUFFER, this.framebuffer);
    if (enabled) {
      this.depthTexture = gl.createTexture();
      this.allocateDepthTexture(gl);
    } else {
      gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.DEPTH_STENCIL_ATTACHMENT, gl.TEXTURE_2D, null, 0);
      gl.deleteTexture(this.depthTexture);
      this.depthTexture = null;
    }
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
  }

  /** Depth24/stencil8 matches the multisampled renderbuffer format, which a depth blit requires. */
  private allocateDepthTexture(gl: WebGL2RenderingContext): void {
    if (this.depthTexture === null || this.framebuffer === null) {
      return;
    }
    gl.bindTexture(gl.TEXTURE_2D, this.depthTexture);
    gl.texImage2D(
      gl.TEXTURE_2D,
      0,
      gl.DEPTH24_STENCIL8,
      this.width,
      this.height,
      0,
      gl.DEPTH_STENCIL,
      gl.UNSIGNED_INT_24_8,
      null
    );
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.bindTexture(gl.TEXTURE_2D, null);
    gl.bindFramebuffer(gl.FRAMEBUFFER, this.framebuffer);
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.DEPTH_STENCIL_ATTACHMENT, gl.TEXTURE_2D, this.depthTexture, 0);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
  }

  private resizeTexture(gl: WebGL2RenderingContext): void {
    if (this.texture === null) {
      return;
    }
    gl.bindTexture(gl.TEXTURE_2D, this.texture);
    const level: number = 0;
    const internalFormat: number = gl.RGBA;
    const border: number = 0;
    const format: number = gl.RGBA;
    const type: number = gl.UNSIGNED_BYTE;
    gl.texImage2D(gl.TEXTURE_2D, level, internalFormat, this.width, this.height, border, format, type, null);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.bindTexture(gl.TEXTURE_2D, null);
  }
}
